import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { API_URL } from '../constants/urls';

// Types
export interface OKRRelationship {
  id: string;
  okr_id: string;
  relationship_type:
    | 'position'
    | 'segment'
    | 'proposition'
    | 'group'
    | 'organization';
  related_object_id: string;
  contribution_notes?: string;
  created_at: string;
  related_object_name?: string;
}

export interface OKRRelationshipCreate {
  okr_id: string;
  relationship_type:
    | 'position'
    | 'segment'
    | 'proposition'
    | 'group'
    | 'organization';
  related_object_id: string;
  contribution_notes?: string;
}

export interface MasterPlanTask {
  id: string;
  organization_id: string;
  planning_cycle_id?: string;
  title: string;
  description?: string;
  area_id?: string;
  area_name?: string;
  assigned_to_id?: string;
  assigned_to_name?: string;
  status: 'not_started' | 'in_progress' | 'complete' | 'blocked';
  priority: 'high' | 'medium' | 'low';
  // time_period removed - use due_date instead
  due_date?: string;
  completed_date?: string;
  okr_id?: string;
  okr_objective?: string;
  key_result_id?: string;
  key_result_description?: string;
  context_type?:
    | 'organization'
    | 'position'
    | 'segment'
    | 'proposition'
    | 'group'
    | 'company';
  context_object_id?: string;
  context_object_name?: string;
  context_object_type?: string; // Added for backward compatibility
  gravity_plan_id?: string;
  display_order: number;
  notes?: string;
  reference_number?: number; // Human-friendly reference like TASK-789
  formatted_reference?: string; // Formatted as "TASK-789"
  created_at: string;
  updated_at: string;
  created_by_id?: string;
}

export interface MasterPlanTaskCreate {
  title: string;
  description?: string;
  area_id?: string;
  assigned_to_id?: string;
  status?: 'not_started' | 'in_progress' | 'complete' | 'blocked';
  priority?: 'high' | 'medium' | 'low';
  // time_period removed - use due_date instead
  due_date?: string;
  okr_id?: string;
  key_result_id?: string;
  context_type?:
    | 'organization'
    | 'position'
    | 'segment'
    | 'proposition'
    | 'group'
    | 'company';
  context_object_id?: string;
  notes?: string;
}

export interface MasterPlanTaskUpdate {
  title?: string;
  description?: string;
  area_id?: string;
  assigned_to_id?: string;
  status?: 'not_started' | 'in_progress' | 'complete' | 'blocked';
  priority?: 'high' | 'medium' | 'low';
  // time_period removed - use due_date instead
  due_date?: string;
  completed_date?: string;
  okr_id?: string;
  key_result_id?: string;
  context_type?:
    | 'organization'
    | 'position'
    | 'segment'
    | 'proposition'
    | 'group'
    | 'company';
  context_object_id?: string;
  notes?: string;
  display_order?: number;
}

export interface MasterPlanFilter {
  okr_ids?: string[];
  position_ids?: string[];
  focus_modes?: string[];
  segment_ids?: string[];
  proposition_ids?: string[];
  group_ids?: string[];
  area_ids?: string[];
  owner_ids?: string[];
  statuses?: string[];
  priorities?: string[];
  company_ids?: string[];
  planning_cycle_id?: string;
  // time_periods removed - use due_date filtering instead
  include_org_tasks?: boolean;
  search?: string;
}

export interface MasterPlanTaskBulkUpdate {
  task_ids: string[];
  updates: MasterPlanTaskUpdate;
}

export interface MasterPlanTaskBulkMove {
  task_ids: string[];
  context_type:
    | 'organization'
    | 'position'
    | 'segment'
    | 'proposition'
    | 'group'
    | 'company';
  context_object_id: string;
  // time_period removed - use due_date instead
}

// Helper function to build query string from filter object
function buildFilterQuery(filters: MasterPlanFilter): string {
  const params = new URLSearchParams();

  if (filters.okr_ids?.length) {
    filters.okr_ids.forEach((id) => params.append('okr_ids', id));
  }
  if (filters.position_ids?.length) {
    filters.position_ids.forEach((id) => params.append('position_ids', id));
  }
  if (filters.focus_modes?.length) {
    filters.focus_modes.forEach((mode) => params.append('focus_modes', mode));
  }
  if (filters.segment_ids?.length) {
    filters.segment_ids.forEach((id) => params.append('segment_ids', id));
  }
  if (filters.proposition_ids?.length) {
    filters.proposition_ids.forEach((id) =>
      params.append('proposition_ids', id),
    );
  }
  if (filters.group_ids?.length) {
    filters.group_ids.forEach((id) => params.append('group_ids', id));
  }
  if (filters.area_ids?.length) {
    filters.area_ids.forEach((id) => params.append('area_ids', id));
  }
  if (filters.owner_ids?.length) {
    filters.owner_ids.forEach((id) => params.append('owner_ids', id));
  }
  if (filters.statuses?.length) {
    filters.statuses.forEach((status) => params.append('statuses', status));
  }
  if (filters.priorities?.length) {
    filters.priorities.forEach((priority) =>
      params.append('priorities', priority),
    );
  }
  if (filters.company_ids?.length) {
    filters.company_ids.forEach((id) => params.append('company_ids', id));
  }
  if (filters.planning_cycle_id) {
    params.append('planning_cycle_id', filters.planning_cycle_id);
  }
  // time_periods removed - use due_date filtering instead
  if (filters.include_org_tasks !== undefined) {
    params.append('include_org_tasks', filters.include_org_tasks.toString());
  }
  if (filters.search) {
    params.append('search', filters.search);
  }

  return params.toString();
}

// OKR Relationship Service
export const okrRelationshipService = {
  async create(
    organizationId: string,
    relationship: OKRRelationshipCreate,
  ): Promise<OKRRelationship> {
    const response = await axios.post(
      `${API_URL}/master-plan/okr-relationships?organization_id=${organizationId}`,
      relationship,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  async list(
    organizationId: string,
    okrId?: string,
    relationshipType?: string,
    relatedObjectId?: string,
  ): Promise<OKRRelationship[]> {
    let url = `${API_URL}/master-plan/okr-relationships?organization_id=${organizationId}`;
    if (okrId) url += `&okr_id=${okrId}`;
    if (relationshipType) url += `&relationship_type=${relationshipType}`;
    if (relatedObjectId) url += `&related_object_id=${relatedObjectId}`;

    const response = await axios.get(url, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  async delete(organizationId: string, relationshipId: string): Promise<void> {
    await axios.delete(
      `${API_URL}/master-plan/okr-relationships/${relationshipId}?organization_id=${organizationId}`,
      {
        headers: getAuthHeaders(),
      },
    );
  },
};

// Master Plan Task Service
export const masterPlanTaskService = {
  async create(
    organizationId: string,
    task: MasterPlanTaskCreate,
  ): Promise<MasterPlanTask> {
    const response = await axios.post(
      `${API_URL}/master-plan/tasks?organization_id=${organizationId}`,
      task,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  async list(
    organizationId: string,
    filters: MasterPlanFilter = {},
  ): Promise<MasterPlanTask[]> {
    const queryString = buildFilterQuery(filters);
    const url = `${API_URL}/master-plan/tasks?organization_id=${organizationId}${queryString ? '&' + queryString : ''}`;

    const response = await axios.get(url, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  async get(organizationId: string, taskId: string): Promise<MasterPlanTask> {
    const response = await axios.get(
      `${API_URL}/master-plan/tasks/${taskId}?organization_id=${organizationId}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  async update(
    organizationId: string,
    taskId: string,
    updates: MasterPlanTaskUpdate,
  ): Promise<MasterPlanTask> {
    const response = await axios.put(
      `${API_URL}/master-plan/tasks/${taskId}?organization_id=${organizationId}`,
      updates,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  async delete(organizationId: string, taskId: string): Promise<void> {
    await axios.delete(
      `${API_URL}/master-plan/tasks/${taskId}?organization_id=${organizationId}`,
      {
        headers: getAuthHeaders(),
      },
    );
  },

  async bulkUpdate(
    organizationId: string,
    bulkUpdate: MasterPlanTaskBulkUpdate,
  ): Promise<{ detail: string }> {
    const response = await axios.post(
      `${API_URL}/master-plan/tasks/bulk-update?organization_id=${organizationId}`,
      bulkUpdate,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  async bulkMove(
    organizationId: string,
    bulkMove: MasterPlanTaskBulkMove,
  ): Promise<{ detail: string }> {
    const response = await axios.post(
      `${API_URL}/master-plan/tasks/bulk-move?organization_id=${organizationId}`,
      bulkMove,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },
};

// Helper to get task status color
export function getTaskStatusColor(status: string): string {
  switch (status) {
    case 'Complete':
      return '#10b981'; // green
    case 'In Progress':
      return '#3b82f6'; // blue
    case 'Blocked':
      return '#ef4444'; // red
    case 'Not Started':
    default:
      return '#6b7280'; // gray
  }
}

// Helper to get task priority color
export function getTaskPriorityColor(priority: string): string {
  switch (priority) {
    case 'High':
      return '#ef4444'; // red
    case 'Medium':
      return '#f59e0b'; // amber
    case 'Low':
      return '#10b981'; // green
    default:
      return '#6b7280'; // gray
  }
}

// TIME_PERIODS removed - use due_date filtering instead
